import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { openSignedUrl, openStorageObject } from '@/lib/openSignedUrl';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { format, differenceInDays } from 'date-fns';
import { formatBusinessDate } from '@/lib/format';
import { FileText, CreditCard, ExternalLink, CheckCircle, Clock, FileSignature, Car, Printer } from 'lucide-react';
import RegistrationForm from '@/components/contracts/preparation/RegistrationForm';
import { fetchAgreementDocuments, type AgreementDocumentRow } from '@/lib/agreements/api';
import { DOC_TYPES, type AgreementDocType } from '@/lib/agreements/constants';
import { unifyAgreementDocuments, type PaperAgreement } from '@/lib/agreements/unifiedAgreements';
import { registrationTypeLabel } from '@/lib/registration';

interface ContractInsightDocumentsProps {
    data: ContractInsightsData;
    vehicleId: number;
    onRefresh?: () => void;
}

const ID_TYPE_LABELS: Record<string, string> = {
    DRIVING_LICENCE: 'Driving Licence',
    NATIONAL_ID: 'National ID',
};

/** The e-signing vocabulary's entry for a type, whichever generation asked for it. */
const docTypeMeta = (docType: string) => DOC_TYPES[docType as AgreementDocType] ?? null;

/**
 * The document families this tab shows — the three arcs it has always drawn, and the Vehicle
 * Schedule.
 *
 * `get_agreement_documents` returns a contract's WHOLE signing history, loan and insurance-loan
 * documents included, but a loan's record lives on the Loans tab: it draws the loan's legacy row,
 * which `agreement_finalize` writes the executed PDF back onto. Listing those here as well would
 * print one agreement on two tabs and split a loan's story across both.
 *
 * THE SCHEDULE IS HERE BECAUSE NOTHING ELSE ON THIS PAGE DRAWS IT [D-B48]. It is an executed
 * instrument about this contract's vehicle, `get_agreement_documents` hands it to admins, and no
 * tab holds a partner's legacy rows the way the Loans tab holds a loan's — so leaving it out would
 * be the one signed document on the contract with no record view at all.
 *
 * NO LEGACY TWIN IS DEDUPLICATED AGAINST. `get_contract_insights` projects no
 * `contract_partner_agreements`, so `documentArcKey` answers null for the type and an in-app
 * schedule prints once, as itself. A schedule that exists only as a pre-app scan does not appear
 * on this tab at all; its home is the setup canvas and the partner's own record.
 */
const TAB_DOC_TYPES = new Set<string>([
    'HIRE_PURCHASE',
    'WITNESS',
    'DEPOSIT',
    'PARTNER_MANAGEMENT',
]);

/**
 * How a document's status reads on a record view.
 *
 * The three tones are this file's existing badge idiom: green for something that is done, amber
 * for something still moving, and a plain outline for a document that binds nobody. There is no
 * "Pending" green/amber pair any more — an agreement that has not been signed at all is work on
 * the setup board, and this tab shows the record.
 */
function documentBadge(document: AgreementDocumentRow): {
    label: string;
    tone: 'signed' | 'moving' | 'withdrawn' | 'closed';
} {
    switch (document.status) {
        case 'COMPLETED':
            // Signed by everyone, but the executed PDF is a separate step: `agreement_finalize`
            // stamps `finalized_at` only once the object is in the bucket.
            return { label: document.finalized_at ? 'Complete' : 'Signed', tone: 'signed' };
        case 'IN_SIGNING':
            return { label: 'In signing', tone: 'moving' };
        case 'PENDING_REVIEW':
            return { label: 'Being prepared', tone: 'moving' };
        case 'VOIDED':
            return { label: 'Withdrawn', tone: 'withdrawn' };
        case 'SUPERSEDED':
            return { label: 'Replaced', tone: 'closed' };
        // A status this app has not met yet is reported verbatim rather than as a guess.
        default:
            return { label: document.status, tone: 'closed' };
    }
}

/** What to say where there is no file to open yet. One line, never a signing CTA. */
function documentNote(document: AgreementDocumentRow): string {
    switch (document.status) {
        case 'COMPLETED':
            return 'Every party signed — the executed PDF is still being sealed.';
        case 'IN_SIGNING':
            return 'Out for signature.';
        case 'PENDING_REVIEW':
            return 'Being prepared on the agreements board.';
        case 'VOIDED':
            return 'Withdrawn — nobody is bound by this version.';
        case 'SUPERSEDED':
            return 'Replaced by a newer version.';
        default:
            return 'No file on record for this document.';
    }
}

export default function ContractInsightDocuments({ data, vehicleId, onRefresh }: ContractInsightDocumentsProps) {
    const [editingRegistration, setEditingRegistration] = useState<number | null>(null);
    const driverIdentification = data.driver_identification || [];
    const hasIdentification = driverIdentification.length > 0;

    // The legacy rows, both arcs, exactly as `get_contract_insights` returns them.
    const contractAgreements = data.agreements || [];
    const depositAgreements = data.deposit_agreements || [];
    const contractId = data.contract?.id ?? null;

    // Vehicle registrations
    const vehicleRegistrations = data.vehicle_registrations || [];
    const hasRegistrations = vehicleRegistrations.length > 0;

    /**
     * The in-app documents, under the key the signing board already caches them on — the two
     * surfaces read the same list and a second key would let them disagree.
     */
    const {
        data: documentRows,
        error: documentsError,
        isLoading: documentsLoading,
    } = useQuery({
        queryKey: ['agreement-documents', contractId],
        queryFn: () => fetchAgreementDocuments(contractId as number),
        enabled: !!contractId,
    });

    /**
     * A FAILED READ IS AN EMPTY LIST, NOT AN EMPTY TAB. The paperwork on file predates the
     * e-signing subsystem and does not depend on it, so an unavailable RPC costs the in-app
     * cards and nothing else — `[]` is what makes every legacy row fall through as unmatched,
     * and the record stays readable.
     *
     * THE DEGRADED PAGE IS NOT THE OLD PAGE, AND THAT IS ACCEPTED. `agreement_finalize` writes
     * `signed_at` and `agreement_url` back onto the legacy row, so an agreement executed IN THE
     * APP has a legacy row too — with no documents to match it against it is drawn as a "Paper"
     * card, badged as signed on paper while pointing at the executed PDF. A wrong badge on a
     * failed read costs less than a tab with no agreements on it, and it is what the driver app
     * does with the same read.
     */
    const documents =
        documentsError || !documentRows
            ? []
            : documentRows.filter((row) => TAB_DOC_TYPES.has(row.doc_type));

    const { live, signed } = unifyAgreementDocuments({
        documents,
        contractAgreements,
        depositAgreements,
    });

    const hasAgreementCards = live.length > 0 || signed.length > 0;
    /**
     * Dedupe cannot be decided until the documents arrive: drawing the legacy rows first would
     * print the very duplicates this section exists to remove, and then remove them under the
     * reader. `agreement_create` writes the legacy row for every arc it raises a document on, and
     * this tab is scoped to the hire purchase, witness and deposit arcs — so a contract with no
     * legacy contract-agreement and no deposit row has no in-app document for any arc drawn here,
     * and there is nothing for a skeleton to stand in for. (It may still hold loan documents;
     * those are the Loans tab's to wait for.)
     */
    const hasLegacyAgreements = contractAgreements.length > 0 || depositAgreements.length > 0;
    const agreementsLoading = documentsLoading && hasLegacyAgreements;

    // Currency formatter
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
    };

    // Helper to get expiry status for ID documents
    const getExpiryStatus = (expiresAt: string | null) => {
        if (!expiresAt) return { label: 'No Expiry', variant: 'secondary' as const };
        const daysUntil = differenceInDays(new Date(expiresAt), new Date());
        if (daysUntil < 0) return { label: 'Expired', variant: 'destructive' as const };
        if (daysUntil <= 30) return { label: `Expires in ${daysUntil} days`, variant: 'warning' as const };
        return { label: 'Valid', variant: 'success' as const };
    };

    /**
     * COUNTED ON WHAT WILL ACTUALLY BE DRAWN, not on what is in the payload. A legacy row with
     * no signature and no document produces no card under the unified rules, so counting the row
     * itself would leave a tab with nothing on it and no explanation of why.
     */
    if (!hasIdentification && !hasAgreementCards && !agreementsLoading && !hasRegistrations) {
        return (
            <div className="border rounded-xl p-12 text-center bg-muted/20">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p className="text-lg font-medium mt-4">No Documents</p>
                <p className="text-sm text-muted-foreground mt-1">
                    No identity documents, signed agreements, or registrations recorded for this contract.
                </p>
            </div>
        );
    }

    /**
     * One in-app document. The card family is the identity card's, so the two generations of a
     * record read the same — what differs is the badge saying how it was signed, and the paper
     * card below carries the same chrome with a "Paper" badge instead.
     *
     * NO SIGNING CTA, DELIBERATELY. Generating, sending, countersigning and voiding all live on
     * the setup board; this tab presents the record and opens the file.
     */
    const renderDocumentCard = (document: AgreementDocumentRow) => {
        const meta = docTypeMeta(document.doc_type);
        const Icon = meta?.icon ?? FileSignature;
        const badge = documentBadge(document);
        // Both halves or neither: the executed object is only reported once it is sealed.
        const finalObject =
            document.final_bucket && document.final_path
                ? { bucket: document.final_bucket, path: document.final_path }
                : null;

        return (
            <Card key={`doc-${document.id}`} className="overflow-hidden">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 rounded-lg bg-muted text-foreground">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <CardTitle className="text-sm">
                                    {meta?.label ?? document.title}
                                </CardTitle>
                                <CardDescription className="text-xs">
                                    <span className="font-mono">{document.reference_no}</span>
                                    {document.completed_at && (
                                        <> · Signed {format(new Date(document.completed_at), 'MMM d, yyyy')}</>
                                    )}
                                </CardDescription>
                            </div>
                        </div>
                        {badge.tone === 'signed' ? (
                            <Badge className="bg-green-500 shrink-0">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {badge.label}
                            </Badge>
                        ) : badge.tone === 'moving' ? (
                            <Badge variant="secondary" className="shrink-0 bg-amber-100 text-amber-700 border-amber-300">
                                <Clock className="h-3 w-3 mr-1" />
                                {badge.label}
                            </Badge>
                        ) : (
                            <Badge
                                variant={badge.tone === 'withdrawn' ? 'destructive' : 'outline'}
                                className="shrink-0"
                            >
                                {badge.label}
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    {finalObject ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => openStorageObject(finalObject.bucket, finalObject.path)}
                        >
                            View Agreement
                            <ExternalLink className="h-3 w-3 ml-2" />
                        </Button>
                    ) : (
                        <p className="text-xs text-muted-foreground">{documentNote(document)}</p>
                    )}
                </CardContent>
            </Card>
        );
    };

    /**
     * One legacy scan — a record signed before the app could take a signature, or one recorded
     * on paper since. It takes the title of the document type it would have been, so a driver
     * agreement now reads "Hire Purchase Agreement" whichever way it was signed, and the deposit
     * card keeps the money and the paid date in the subline it used to carry in its title.
     */
    const renderPaperCard = (paper: PaperAgreement) => {
        const meta = docTypeMeta(paper.docType);
        const Icon = meta?.icon ?? FileSignature;

        return (
            <Card key={paper.key} className="overflow-hidden">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 rounded-lg bg-muted text-foreground">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <CardTitle className="text-sm">
                                    {meta?.label ?? paper.docType}
                                </CardTitle>
                                <CardDescription className="text-xs tabular-nums">
                                    {paper.depositAmount !== null && <>{formatCurrency(paper.depositAmount)} · </>}
                                    {/* `depositDate` is `transactions.transaction_at` renamed through the RPC json — a
                                        money instant, so the Ghana day it was paid on, never the viewer's. */}
                                    {paper.depositDate && <>Paid {formatBusinessDate(paper.depositDate)} · </>}
                                    Signed {format(new Date(paper.signedAt), 'MMM d, yyyy')}
                                </CardDescription>
                            </div>
                        </div>
                        <Badge variant="outline" className="shrink-0 gap-1 font-normal">
                            <Printer className="h-3 w-3" />
                            Paper
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    {paper.url ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => openSignedUrl(paper.url!)}
                        >
                            View Scan
                            <ExternalLink className="h-3 w-3 ml-2" />
                        </Button>
                    ) : (
                        <p className="text-xs text-muted-foreground">
                            Signed on paper — no scan uploaded.
                        </p>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-8">
            {/* Identity Documents Section */}
            {hasIdentification && (
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        Identity Documents
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {driverIdentification.map((doc) => {
                            const status = getExpiryStatus(doc.expires_at);
                            return (
                                <Card key={doc.id} className="overflow-hidden">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg bg-muted text-foreground`}>
                                                    <CreditCard className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-sm">
                                                        {ID_TYPE_LABELS[doc.type] || doc.type}
                                                    </CardTitle>
                                                    {doc.expires_at && (
                                                        <CardDescription className="text-xs">
                                                            Expires {format(new Date(doc.expires_at), 'MMM d, yyyy')}
                                                        </CardDescription>
                                                    )}
                                                </div>
                                            </div>
                                            <Badge
                                                variant={status.variant === 'success' ? 'default' : status.variant === 'warning' ? 'outline' : 'destructive'}
                                                className={status.variant === 'success' ? 'bg-green-500' : status.variant === 'warning' ? 'border-amber-500 text-amber-600' : ''}
                                            >
                                                {status.label}
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    {doc.document_url && (
                                        <CardContent className="pt-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="w-full"
                                                onClick={() => openSignedUrl(doc.document_url!)}
                                            >
                                                View Document
                                                <ExternalLink className="h-3 w-3 ml-2" />
                                            </Button>
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                </div>
            )}

            {/*
              * Agreements — ONE section, both generations, deduped by arc.
              *
              * A record signed in the app and the legacy row underneath it are the same
              * agreement: `agreement_finalize` writes the executed PDF's URL back onto that row,
              * so the two separate sections this replaces printed the same PDF twice. The
              * in-app card is the canonical one wherever a document holds the arc; a scan on an
              * arc no document holds — or one a document was withdrawn from — keeps its card.
              */}
            {(agreementsLoading || hasAgreementCards) && (
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        Agreements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {agreementsLoading ? (
                            <>
                                <Skeleton className="h-32 w-full" />
                                <Skeleton className="h-32 w-full" />
                            </>
                        ) : (
                            <>
                                {/* Still in flight, newest first, exactly as the server ordered them. */}
                                {live.map(renderDocumentCard)}
                                {signed.map((entry) =>
                                    entry.kind === 'document'
                                        ? renderDocumentCard(entry.document)
                                        : renderPaperCard(entry.paper),
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Vehicle Registrations Section */}
            {hasRegistrations && (
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        Vehicle Registrations
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vehicleRegistrations.map((reg) => (
                            <Card key={reg.id} className="overflow-hidden">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-muted text-foreground">
                                                <Car className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm">
                                                    {registrationTypeLabel(reg.type)}
                                                </CardTitle>
                                                <CardDescription className="text-xs">
                                                    {reg.number}
                                                </CardDescription>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className="bg-green-500">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Registered
                                            </Badge>
                                        </div>
                                    </div>
                                </CardHeader>
                                {reg.certificate_url && (
                                    <CardContent className="pt-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full"
                                            onClick={() => openSignedUrl(reg.certificate_url!)}
                                        >
                                            View Certificate
                                            <ExternalLink className="h-3 w-3 ml-2" />
                                        </Button>
                                    </CardContent>
                                )}
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Registration Edit Form */}
            <RegistrationForm
                open={editingRegistration !== null}
                onOpenChange={(open) => {
                    if (!open) setEditingRegistration(null);
                }}
                vehicleId={vehicleId}
                onSuccess={() => {
                    setEditingRegistration(null);
                    onRefresh?.();
                }}
            />
        </div>
    );
}
